import { Stack, Typography } from "@mui/material";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import { NameBasedMaterialLimit } from "store/api/generatedApi";
import { formatSignatureLabel } from "components/defaultParameters/materialLimitMatrix";

export const MaterialLimitSummary = ({
  limit,
}: {
  limit: NameBasedMaterialLimit;
}) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(false);
  const format = useFormatNumber()(3);
  const quantity = formatSignatureLabel({
    coefficients: limit.coefficients.map((coefficient, index) => ({
      material_constraint_class_id: index,
      coefficient: coefficient.coefficient,
    })),
    classNameById: Object.fromEntries(
      limit.coefficients.map((coefficient, index) => [
        index,
        coefficient.material_constraint_class_name,
      ])
    ),
    translate: (name) => t(name),
    formatNumber: format,
  });
  const mass = (bound: number) => `${format(bound)}${units("mass")}`;
  return (
    <Stack gap={0.5}>
      {limit.bounds.map((bounds) => {
        const scope =
          bounds.basket_number === null
            ? t("mix")
            : `${t("basket")} ${format(bounds.basket_number)}`;
        const hard =
          bounds.min_mass === null && bounds.max_mass === null
            ? bounds.soft_min_mass === null && bounds.soft_max_mass === null
              ? `${quantity} (${t("none")})`
              : quantity
            : bounds.min_mass !== null && bounds.min_mass === bounds.max_mass
              ? `${quantity} = ${mass(bounds.min_mass)}`
              : `${bounds.min_mass !== null ? `${mass(bounds.min_mass)} ≤ ` : ""}${quantity}${bounds.max_mass !== null ? ` ≤ ${mass(bounds.max_mass)}` : ""}`;
        const soft = [
          bounds.soft_min_mass !== null
            ? `${t("softMin")}: ${mass(bounds.soft_min_mass)}`
            : null,
          bounds.soft_max_mass !== null
            ? `${t("softMax")}: ${mass(bounds.soft_max_mass)}`
            : null,
        ].filter((text) => text !== null);
        return (
          <Typography
            key={bounds.basket_number ?? "mix"}
            variant="body2Mono"
          >{`${scope}: ${hard}${soft.length ? `; ${soft.join("; ")}; ${t("hardness")}: ${format(limit.hardness)}` : ""}`}</Typography>
        );
      })}
      {limit.bounds.length === 0 ? (
        <Typography variant="body2Mono">{`${quantity} (${t("none")})`}</Typography>
      ) : null}
    </Stack>
  );
};
